import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { useTrans } from '../../Store/System/LocalizationStore';
import { FooterLanguageSelect } from './FooterLanguageSelect';
import { Link } from 'react-router-dom';
import { useRootStore } from '../../Store/useRootStore';
import { Theme } from '../../Theme/Theme';
import { useLanguage } from '../../Hooks/useLanguage';

const MainFooter = styled.footer`
  background: linear-gradient(180deg, #1a1a1a 0%, #000000 100%);
  color: white;
  position: relative;
`;

const FooterTop = styled.div`
  padding: 48px 24px 32px;
  max-width: 1200px;
  margin: 0 auto;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr repeat(4, 1fr);
  gap: 48px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 32px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const BrandColumn = styled.div`
  @media (max-width: 1024px) {
    grid-column: 1 / -1;
    margin-bottom: 16px;
  }
`;

const FooterLogo = styled.img`
  height: 40px;
  margin-bottom: 16px;
`;

const BrandTagline = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  margin: 0 0 20px 0;
  max-width: 280px;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 12px;
`;

const SocialLink = styled.a`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-decoration: none;
  transition: all 0.2s;

  &:hover {
    background: ${Theme.Colors.primary};
    transform: translateY(-2px);
  }

  i {
    font-size: 18px;
  }
`;

const FooterColumn = styled.div``;

const ColumnTitle = styled.h4`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0 0 16px 0;
`;

const LinkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const LinkItem = styled.li`
  margin-bottom: 10px;
`;

const FooterLink = styled(Link)`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  transition: all 0.2s;

  &:hover {
    color: ${Theme.Colors.primary};
  }
`;

const FooterExternalLink = styled.a`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  transition: all 0.2s;

  &:hover {
    color: ${Theme.Colors.primary};
  }
`;

const FooterButton = styled.button`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  transition: all 0.2s;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;

  &:hover {
    color: ${Theme.Colors.primary};
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 20px 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const BottomContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 600px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Copyright = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
`;

const LanguageWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export type FooterProps = {};

export const Footer: React.FC<FooterProps> = observer(() => {
  const { coachSubscription } = useRootStore();
  const { t } = useTrans();
  const { language } = useLanguage();

  const privacyLinkEnglish = 'https://www.kinastic.com/privacy-policy-blank/';
  const privacyLinkGerman = 'https://www.kinastic.com/datenschutzerklarung-blank/';
  const privacyPolicyLink = language === 'en' ? privacyLinkEnglish : privacyLinkGerman;

  const termsLinkEnglish = 'https://www.kinastic.com/agb-app-en/';
  const termsLinkGerman = 'https://www.kinastic.com/agb-app/';
  const termsLink = language === 'en' ? termsLinkEnglish : termsLinkGerman;

  const currentYear = new Date().getFullYear();

  return (
    <MainFooter>
      <FooterTop>
        <FooterGrid>
          <BrandColumn>
            <a href="https://coach.kinastic.com">
              <FooterLogo src={require('../../Assets/Images/kinastic-logo.png')} alt="KINASTIC" />
            </a>
            <BrandTagline>
              {t('footer.tagline', { defaultValue: 'Your personal health & fitness companion for a balanced life.' })}
            </BrandTagline>
            <LanguageWrapper style={{ marginBottom: 16 }}>
              <FooterLanguageSelect />
            </LanguageWrapper>
            <SocialLinks>
              <SocialLink
                href="https://www.instagram.com/kinasticag/"
                target="_blank"
                rel="noopener noreferrer"
                title="Instagram"
              >
                <i className="bi bi-instagram" />
              </SocialLink>
              <SocialLink
                href="https://www.linkedin.com/company/kinastic-ag/"
                target="_blank"
                rel="noopener noreferrer"
                title="LinkedIn"
              >
                <i className="bi bi-linkedin" />
              </SocialLink>
              <SocialLink
                href="https://www.youtube.com/@kinasticag_official"
                target="_blank"
                rel="noopener noreferrer"
                title="YouTube"
              >
                <i className="bi bi-youtube" />
              </SocialLink>
            </SocialLinks>
          </BrandColumn>

          {coachSubscription.canShowMove && (
            <FooterColumn>
              <ColumnTitle>{t('footer.explore', { defaultValue: 'Explore' })}</ColumnTitle>
              <LinkList>
                <LinkItem>
                  <FooterLink to="/move">{t('footer.workouts', { defaultValue: 'Workouts' })}</FooterLink>
                </LinkItem>
                <LinkItem>
                  <FooterLink to="/schedule">{t('footer.mySchedule', { defaultValue: 'My Schedule' })}</FooterLink>
                </LinkItem>
                {coachSubscription.canShowAcademy && (
                  <LinkItem>
                    <FooterLink to="/academy">{t('footer.academy', { defaultValue: 'Academy' })}</FooterLink>
                  </LinkItem>
                )}
              </LinkList>
            </FooterColumn>
          )}

          <FooterColumn>
            <ColumnTitle>{t('footer.account', { defaultValue: 'Account' })}</ColumnTitle>
            <LinkList>
              <LinkItem>
                <FooterLink to="/account#profile">{t('footer.profile', { defaultValue: 'Profile' })}</FooterLink>
              </LinkItem>
              <LinkItem>
                <FooterLink to="/account#body">{t('footer.bodyData', { defaultValue: 'Body Data' })}</FooterLink>
              </LinkItem>
              <LinkItem>
                <FooterLink to="/account#programs">{t('footer.programs', { defaultValue: 'My Programs' })}</FooterLink>
              </LinkItem>
              <LinkItem>
                <FooterLink to="/account#settings">{t('footer.settings', { defaultValue: 'Settings' })}</FooterLink>
              </LinkItem>
            </LinkList>
          </FooterColumn>

          <FooterColumn>
            <ColumnTitle>{t('footer.company', { defaultValue: 'Company' })}</ColumnTitle>
            <LinkList>
              <LinkItem>
                <FooterExternalLink href="https://www.kinastic.com" target="_blank" rel="noopener noreferrer">
                  {t('footer.aboutUs', { defaultValue: 'About Us' })}
                </FooterExternalLink>
              </LinkItem>
              <LinkItem>
                <FooterExternalLink href="mailto:hello@kinastic.com">
                  {t('footer.contact', { defaultValue: 'Contact' })}
                </FooterExternalLink>
              </LinkItem>
              <LinkItem>
                <FooterExternalLink href="https://www.kinastic.com/kontakt" target="_blank" rel="noopener noreferrer">
                  {t('footer.feedback', { defaultValue: 'Give Feedback' })}
                </FooterExternalLink>
              </LinkItem>
            </LinkList>
          </FooterColumn>

          <FooterColumn>
            <ColumnTitle>{t('footer.legal', { defaultValue: 'Legal' })}</ColumnTitle>
            <LinkList>
              <LinkItem>
                <FooterExternalLink href={privacyPolicyLink} target="_blank" rel="noopener noreferrer">
                  {t('footer.privacy', { defaultValue: 'Privacy Policy' })}
                </FooterExternalLink>
              </LinkItem>
              <LinkItem>
                <FooterExternalLink href={termsLink} target="_blank" rel="noopener noreferrer">
                  {t('footer.terms', { defaultValue: 'Terms of Service' })}
                </FooterExternalLink>
              </LinkItem>
              <LinkItem>
                <FooterExternalLink
                  href="https://www.kinastic.com/kontakt"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('footer.imprint', { defaultValue: 'Imprint' })}
                </FooterExternalLink>
              </LinkItem>
            </LinkList>
          </FooterColumn>
        </FooterGrid>
      </FooterTop>

      <FooterBottom>
        <BottomContent>
          <Copyright>
            &copy; {currentYear} KINASTIC AG. {t('footer.allRightsReserved', { defaultValue: 'All rights reserved.' })}
          </Copyright>
        </BottomContent>
      </FooterBottom>
    </MainFooter>
  );
});
